import { DateTime } from 'luxon';
import React, { memo, Suspense, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AccessContainer from '../components/AccessContainer';
import SettingLoader from '../components/settingLoader';
import { FORMAT_DATE } from '../const';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { WithInitialUrlParams } from '../routing/withInitialUrlParams';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterGantLoader } from '../store/slices/gantLoaderSlice';
import { ItemTaskType, LoaderDataType } from '../types';
import GantLoadListItem from './gantLoadListItem';
import GantLoadScheduleItem from './gantLoadScheduleItem';
import GantLoadTask from './gantLoadTask';

interface IGantCalendarLoader {
    onFinishLoadData?: () => void;
    authorId: string;
    isActiveScheduleFilter?: boolean;
}

const GantCalendarLoader: React.FC<IGantCalendarLoader> = ({ onFinishLoadData, authorId, isActiveScheduleFilter }) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const updateSettingFetchKey = useSelector<RootState, number>((state) => state.setting.updateSettingFetchKey);

    const dataTaskFromServer = useSelector<RootState, LoaderDataType<ItemTaskType>>(
        (state) => state.gantLoader.dataTaskFromServer,
    );

    useEffect(() => {
        if (dataTaskFromServer.connectionId) {
            const processedTasks = dataTaskFromServer.rows.map((row) => ({
                ...row,
                datetime: DateTime.fromSQL(row.datetime!).toFormat(FORMAT_DATE),
                assignee_for_task: row.assignee_for_task ? (row.assignee_for_task as string)?.split(',') || [] : [],
            }));

            dispatch(
                setterCalendar({
                    processedTasksFromServer: processedTasks?.length ? processedTasks : [],
                }),
            );
        }
    }, [dataTaskFromServer]);

    return (
        <Suspense>
            <WithInitialUrlParams onFinish={onFinishLoadData} authorId={authorId}>
                {!!filterProject?.length && (
                    <>
                        <SettingLoader
                            onUpdateSettingData={(rows: any[], count: number, connectionId: string) => {
                                dispatch(setterGantLoader({ dataSettingFromServer: { rows, count, connectionId } }));
                            }}
                            onUpdateRemoteSettingData={(rows: any[], count: number, connectionId: string) => {
                                dispatch(setterGantLoader({ dataSettingFromServer: { rows, count, connectionId } }));
                            }}
                            updateSettingFetchKey={updateSettingFetchKey}
                        />
                        <GantLoadListItem />
                        <GantLoadScheduleItem isActiveScheduleFilter={isActiveScheduleFilter} />
                        <AccessContainer accessFor={['schedule-task']}>
                            <GantLoadTask />
                        </AccessContainer>
                    </>
                )}
            </WithInitialUrlParams>
        </Suspense>
    );
};

export default memo(GantCalendarLoader);
